import { motion } from "framer-motion";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  DollarSign,
  Target,
  Users,
  TrendingUp,
  Zap,
  BarChart3,
  Shield,
  Clock,
  ArrowRight,
  Loader2,
  AlertCircle,
  Building2,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCharityMeasures } from "../../hooks/useFundMatch";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const MEASURE_CONFIG = {
  opportunities: {
    key: "opportunities",
    label: "M1: Opportunities",
    desc: "Total qualified funding available",
    icon: DollarSign,
    color: "emerald",
    format: (v) => `$${(v / 1_000_000).toFixed(2)}M`,
    unit: "in matched grants",
  },
  expectations: {
    key: "expectations",
    label: "M2: Expectations",
    desc: "Probability-weighted expected award",
    icon: Target,
    color: "blue",
    format: (v) => `$${(v / 1_000).toFixed(0)}K`,
    unit: "expected award",
  },
  survey: {
    key: "survey",
    label: "M3: Survey",
    desc: "Avg funding per competing charity",
    icon: Users,
    color: "violet",
    format: (v) => `$${v.toLocaleString()}`,
    unit: "avg per org",
  },
  outlook: {
    key: "outlook",
    label: "M4: Outlook",
    desc: "Revenue trend year-over-year",
    icon: TrendingUp,
    color: v => v >= 0 ? "emerald" : "red",
    format: (v) => `${v >= 0 ? "+" : ""}${(v * 100).toFixed(1)}%`,
    unit: "YoY change",
    isColor: true,
  },
  leverage: {
    key: "leverage",
    label: "M5: Leverage",
    desc: "Feature alignment with funders",
    icon: Zap,
    color: "amber",
    format: (v) => `${(v * 100).toFixed(0)}%`,
    unit: "alignment score",
  },
  positioning: {
    key: "positioning",
    label: "M6: Positioning",
    desc: "Percentile rank vs matched funders",
    icon: BarChart3,
    color: "cyan",
    format: (v) => `${v.toFixed(1)}th`,
    unit: "percentile",
  },
  efficiency: {
    key: "efficiency",
    label: "M7: Efficiency",
    desc: "Expected return per hour of effort",
    icon: Clock,
    color: "orange",
    format: (v) => `$${v.toLocaleString()}`,
    unit: "per hour",
  },
  assurance: {
    key: "assurance",
    label: "M8: Assurance",
    desc: "High-confidence weighted expected $",
    icon: Shield,
    color: "teal",
    format: (v) => `$${(v / 1_000).toFixed(0)}K`,
    unit: "high-confidence",
  },
};

const PIE_COLORS = ["#14b8a6", "#3B82F6", "#F59E0B", "#8B5CF6", "#f97316", "#22c55e", "#ec4899", "#64748b"];

const MeasureCard = ({ cfg, value }) => {
  const Icon = cfg.icon;
  const color = typeof cfg.color === "function" ? cfg.color(value) : cfg.color;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-zinc-200 p-5 hover:shadow-md transition-all"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-xl bg-${color}-100 flex items-center justify-center flex-shrink-0`}>
          <Icon className={`h-4 w-4 text-${color}-600`} />
        </div>
        <div>
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">{cfg.label}</p>
          <p className="text-xs text-zinc-400">{cfg.desc}</p>
        </div>
      </div>
      <p className={`text-2xl font-heading font-bold text-${color}-600`}>{cfg.format(value)}</p>
      <p className="text-xs text-zinc-400 mt-1">{cfg.unit}</p>
    </motion.div>
  );
};

export const ImpactGrowTab = () => {
  const { user } = useAuth();
  const bn = user?.charity_bn || localStorage.getItem("impactplexus_charity_bn");
  const { data, isLoading, error } = useCharityMeasures(bn, !!bn);

  const measures = data?.measures || {};
  const pieData = Object.entries(measures).slice(0, 8).map(([key, value], i) => ({
    name: MEASURE_CONFIG[key]?.label || key,
    value: Math.abs(value) || 0,
    color: PIE_COLORS[i % PIE_COLORS.length],
  }));

  const topMeasure = Object.entries(measures).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="space-y-6" data-testid="impactgrow-tab">
      {/* Header Summary Card */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="md:col-span-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-white">
          <CardContent className="p-6">
            {isLoading && (
              <div className="flex items-center gap-2 text-zinc-500">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Loading ImpactGrow metrics...</span>
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                <span className="text-sm">Could not load metrics: {error.message}</span>
              </div>
            )}
            {data && (
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-cyan-100 text-cyan-700">{data.province}</Badge>
                    <Badge variant="outline">{data.matched_grants} matched grants</Badge>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-heading font-bold text-zinc-900 mb-1">
                    {data.entity_name || bn}
                  </h2>
                  <p className="text-sm text-zinc-500 mb-4">BN: {data.entity_bn}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-white rounded-lg border border-cyan-200">
                      <p className="text-xs text-zinc-500">Peers in Category</p>
                      <p className="text-xl font-bold text-cyan-700">{data.peers_in_category?.toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border border-cyan-200">
                      <p className="text-xs text-zinc-500">Matched Grants</p>
                      <p className="text-xl font-bold text-cyan-700">{data.matched_grants?.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
                {pieData.length > 0 && (
                  <div className="hidden md:block w-44 h-44">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={2} dataKey="value">
                          {pieData.map((entry, i) => (
                            <Cell key={`cell-${i}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [name, ""]}
                          contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", borderRadius: "8px", fontSize: "11px" }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
            {!data && !isLoading && !error && (
              <div className="text-sm text-zinc-500">
                {bn ? "No measures data available yet." : "No charity BN found — please update your profile."}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top Opportunity</CardTitle>
            <CardDescription>Highest-value measure for your org</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {data && topMeasure && MEASURE_CONFIG[topMeasure[0]] && (
              <>
                <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-100">
                  <p className="text-sm font-medium text-cyan-900">{MEASURE_CONFIG[topMeasure[0]].label}</p>
                  <p className="text-2xl font-bold text-cyan-700 mt-1">{MEASURE_CONFIG[topMeasure[0]].format(topMeasure[1])}</p>
                  <p className="text-xs text-cyan-600 mt-1">{MEASURE_CONFIG[topMeasure[0]].desc}</p>
                </div>
                <div className="p-3 bg-zinc-50 rounded-lg">
                  <p className="text-sm font-medium text-zinc-700">Positioning Score</p>
                  {measures.positioning !== undefined && (
                    <>
                      <Progress value={measures.positioning} className="h-2 mt-2" />
                      <p className="text-xs text-zinc-500 mt-1">{measures.positioning?.toFixed(1)}th percentile</p>
                    </>
                  )}
                </div>
              </>
            )}
            {!data && !isLoading && (
              <p className="text-sm text-zinc-400">Load measures to see top opportunity</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* M1–M8 Measures Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-heading font-semibold text-zinc-900">M1–M8 Impact Measures</h2>
          <Button variant="outline" size="sm">
            Refresh <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
        {isLoading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-zinc-50 rounded-xl border border-zinc-200 p-5 animate-pulse h-28" />
            ))}
          </div>
        )}
        {data && measures && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(MEASURE_CONFIG).map(([key, cfg]) => (
              measures[key] !== undefined ? (
                <MeasureCard key={key} cfg={cfg} value={measures[key]} />
              ) : null
            ))}
          </div>
        )}
      </div>

      {/* CSR Partnerships (static suggestions) */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">CSR Partnership Opportunities</CardTitle>
              <CardDescription>Businesses seeking impact partnerships in your sector</CardDescription>
            </div>
            <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700">
              Explore Partnerships
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { company: "TD Bank Group", type: "Youth Programs", alignment: 89 },
              { company: "Rogers Communications", type: "Digital Literacy", alignment: 82 },
              { company: "Loblaws Companies", type: "Community Health", alignment: 76 },
            ].map((partner, i) => (
              <div key={i} className="p-4 bg-zinc-50 rounded-xl hover:bg-cyan-50 transition-colors cursor-pointer">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-10 h-10 rounded-lg bg-white border border-zinc-200 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-zinc-400" />
                  </div>
                  <Badge variant="outline" className="text-xs">{partner.alignment}% match</Badge>
                </div>
                <p className="font-medium text-zinc-900">{partner.company}</p>
                <p className="text-xs text-zinc-500">{partner.type}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImpactGrowTab;
